/**
 * Convierte una fecha a la zona horaria de Madrid (Europe/Madrid)
 * @param date Fecha en formato ISO string o Date object
 * @returns Date object ajustado a la zona horaria de Madrid
 */
const toMadridTime = (date: string | Date): Date => {
  const d = typeof date === 'string' ? new Date(date) : date;
  // Usar Intl.DateTimeFormat para convertir a zona horaria de Madrid
  const madridDate = new Date(d.toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
  // Ajustar la diferencia de zona horaria
  const offset = d.getTime() - madridDate.getTime();
  return new Date(d.getTime() - offset);
};

/**
 * Formatea una fecha como dd/mm/aaaa usando zona horaria de Madrid
 * @param dateString Fecha en formato ISO string o Date object
 * @returns Fecha formateada como dd/mm/aaaa
 */
export const formatDate = (dateString: string | Date | null | undefined): string => {
  if (!dateString) {
    return '—';
  }

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    if (isNaN(date.getTime())) {
      return '—';
    }

    // Convertir a zona horaria de Madrid
    const madridDate = new Date(date.toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
    
    const day = String(madridDate.getDate()).padStart(2, '0');
    const month = String(madridDate.getMonth() + 1).padStart(2, '0');
    const year = madridDate.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '—';
  }
};

/**
 * Formatea una fecha y hora como dd/mm/aaaa hh:mm usando zona horaria de Madrid
 * @param dateString Fecha en formato ISO string o Date object
 * @returns Fecha y hora formateada como dd/mm/aaaa hh:mm
 */
export const formatDateTime = (dateString: string | Date | null | undefined): string => {
  if (!dateString) {
    return '—';
  }

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    if (isNaN(date.getTime())) {
      return '—';
    }

    // Convertir a zona horaria de Madrid usando Intl.DateTimeFormat
    const formatter = new Intl.DateTimeFormat('es-ES', {
      timeZone: 'Europe/Madrid',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(date);
    const day = parts.find(p => p.type === 'day')?.value || '';
    const month = parts.find(p => p.type === 'month')?.value || '';
    const year = parts.find(p => p.type === 'year')?.value || '';
    const hour = parts.find(p => p.type === 'hour')?.value || '';
    const minute = parts.find(p => p.type === 'minute')?.value || '';

    return `${day}/${month}/${year} ${hour}:${minute}`;
  } catch {
    return '—';
  }
};

/**
 * Formatea solo la hora como hh:mm usando zona horaria de Madrid
 * @param dateString Fecha en formato ISO string o Date object
 * @returns Hora formateada como hh:mm
 */
export const formatTime = (dateString: string | Date | null | undefined): string => {
  if (!dateString) {
    return '—';
  }

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    if (isNaN(date.getTime())) {
      return '—';
    }

    const formatter = new Intl.DateTimeFormat('es-ES', {
      timeZone: 'Europe/Madrid',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    return formatter.format(date);
  } catch {
    return '—';
  }
};

/**
 * Obtiene la fecha actual en zona horaria de Madrid
 * @returns Date object en zona horaria de Madrid
 */
export const getMadridDate = (): Date => {
  const now = new Date();
  // Obtener la fecha/hora en Madrid
  const madridString = now.toLocaleString('en-US', { timeZone: 'Europe/Madrid' });
  return new Date(madridString);
};

/**
 * Convierte una fecha a formato YYYY-MM-DD para inputs de tipo date
 * usando zona horaria de Madrid
 * @param date Fecha en formato Date, string ISO o null/undefined
 * @returns String en formato YYYY-MM-DD o string vacío
 */
export const toDateInputValue = (date: Date | string | null | undefined): string => {
  if (!date) {
    return '';
  }

  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(d.getTime())) {
      return '';
    }

    // Convertir a zona horaria de Madrid
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Madrid',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    return formatter.format(d);
  } catch {
    return '';
  }
};

/**
 * Obtiene la fecha actual en formato YYYY-MM-DD para inputs de tipo date
 * usando zona horaria de Madrid
 * @returns String en formato YYYY-MM-DD
 */
export const getTodayDateInput = (): string => {
  return toDateInputValue(getMadridDate());
};

/**
 * Convierte un string YYYY-MM-DD a Date object interpretándolo como fecha en Madrid
 * @param dateString String en formato YYYY-MM-DD
 * @returns Date object o null si es inválido
 */
export const fromDateInputValue = (dateString: string | null | undefined): Date | null => {
  if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return null;
  }

  try {
    // Interpretar la fecha como medianoche en Madrid
    // Crear fecha en formato ISO con zona horaria de Madrid
    const [year, month, day] = dateString.split('-').map(Number);
    // Crear fecha en UTC que represente medianoche en Madrid
    // Madrid es UTC+1 en invierno, UTC+2 en verano
    // Usamos una aproximación: crear la fecha y luego ajustar
    const date = new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00`);
    
    // Verificar que la fecha es válida
    if (isNaN(date.getTime())) {
      return null;
    }
    
    return date;
  } catch {
    return null;
  }
};

